import { useEffect, useState } from "react";
import { History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, fmtHora } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ACCION_LABEL = {
  fichaje_registrado: "Fichaje registrado",
  creada: "Solicitud creada",
  aprobada: "Solicitud aprobada",
  rechazada: "Solicitud rechazada",
  confirmada_trabajador: "Confirmada por el trabajador",
  rechazada_trabajador: "Rechazada por el trabajador",
  fichaje_registrado_hist: "Fichaje vinculado",
  modificacion_propuesta: "Modificación propuesta",
  modificacion_aplicada: "Modificación aplicada",
  fichaje_registrado_flow: "Fichaje registrado",
};

function labelAccion(accion) {
  return ACCION_LABEL[accion] || accion.replace(/_/g, " ");
}

function resumenRegistrado(f) {
  if (f.registrado_por) return f.registrado_por;
  if (f.origen === "solicitud_aprobada" && f.solicitud_id) return `Vía solicitud #${f.solicitud_id}`;
  if (f.origen === "autocierre") return "Sistema (autocierre)";
  return "—";
}

export default function FichajeTrazabilidadDialog({ fichaje, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!fichaje?.id) return;
    let cancel = false;
    setLoading(true);
    setData(null);
    api
      .get(`/fichajes/${fichaje.id}/trazabilidad`)
      .then(({ data: d }) => {
        if (!cancel) setData(d);
      })
      .catch((e) => {
        if (!cancel) toast.error(formatApiError(e));
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [fichaje?.id]);

  const f = data?.fichaje || fichaje;

  return (
    <Dialog open={!!fichaje} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="fichaje-trazabilidad-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-indigo-600" />
            Trazabilidad del fichaje
          </DialogTitle>
        </DialogHeader>

        {f && (
          <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 px-3 py-2 text-sm">
            <p className="font-medium text-zinc-900">
              {fmtFecha(f.timestamp)} {fmtHora(f.timestamp)} · {f.tipo}
            </p>
            <p className="text-xs text-zinc-500">{f.usuario}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
                {data?.fichaje?.origen_label || (f.es_manual ? "Manual" : "App")}
              </Badge>
              {f.solicitud_id && (
                <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-800">
                  Solicitud #{f.solicitud_id}
                </Badge>
              )}
            </div>
            <p className="mt-2 text-xs text-zinc-600">
              Registrado por: <strong>{resumenRegistrado(data?.fichaje || f)}</strong>
            </p>
            {data?.integridad && (
              <div className="mt-3 rounded-md border border-slate-200 bg-white px-2.5 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Integridad SHA-256
                </p>
                <p className="mt-1 break-all font-mono text-[10px] text-slate-700">
                  {data.integridad.hash_almacenado || "—"}
                </p>
                <Badge
                  variant="outline"
                  className={
                    data.integridad.integridad_ok
                      ? "mt-2 border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "mt-2 border-rose-200 bg-rose-50 text-rose-800"
                  }
                >
                  {data.integridad.integridad_ok ? "Verificado" : "No verificado"}
                </Badge>
                <p className="mt-1 text-[10px] text-slate-400">
                  {data.integridad.formato} · {data.integridad.timestamp_local}
                </p>
              </div>
            )}
          </div>
        )}

        {loading && <p className="py-6 text-center text-sm text-zinc-400">Cargando historial…</p>}

        {!loading && data?.solicitudes?.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Solicitudes vinculadas</p>
            {data.solicitudes.map((s) => (
              <div key={s.id} className="rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2 text-sm">
                <p className="font-medium text-zinc-800">
                  #{s.id} · {s.tipo_label}
                </p>
                <p className="text-xs text-zinc-500">
                  {s.estado.replace(/_/g, " ")}
                  {s.revisado_por ? ` · Revisado por ${s.revisado_por}` : ""}
                </p>
                {s.motivo && <p className="mt-1 text-zinc-600">{s.motivo}</p>}
              </div>
            ))}
          </div>
        )}

        {!loading && data?.eventos?.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Historial</p>
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {data.eventos.map((h, i) => (
                <div key={`${h.id || "e"}-${i}`} className="rounded-lg border border-zinc-100 bg-white px-3 py-2 text-sm">
                  <p className="font-medium text-zinc-800">{labelAccion(h.accion)}</p>
                  <p className="text-xs text-zinc-500">
                    {h.usuario || "—"} · {fmtFecha(h.created_at)} {fmtHora(h.created_at)}
                    {h.solicitud_id ? ` · Sol. #${h.solicitud_id}` : ""}
                  </p>
                  {h.comentario && <p className="mt-1 whitespace-pre-wrap text-zinc-600">{h.comentario}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && data && !data.eventos?.length && (
          <p className="py-4 text-center text-sm text-zinc-400">Sin eventos de trazabilidad adicionales.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
